import { Env } from '../config/env';
import { getBackblazeDownloadUrl } from '../config/backblaze';

const FOLDER_MAP = {
  avatar: 'avatars',
  avatars: 'avatars',
  document: 'ccp_photo',
  documents: 'ccp_photo',
  ccp_photo: 'ccp_photo',
  receipt: 'receipts',
  receipts: 'receipts',
  services: 'services',
  previews: 'previews',
  chat: 'chat',
  recharges: 'recharges',
  pdf: 'pdf',
  zip: 'zip',
};

class BackblazeService {
  constructor() {
    this._authCache = null;
  }

  _resolveFolder(folder, file) {
    if (folder === 'digital') {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext === 'zip') return 'zip';
      return 'pdf';
    }
    return FOLDER_MAP[folder] || folder || 'services';
  }

  _buildFileName(b2Folder, file) {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
    const id = crypto.randomUUID();
    return `${b2Folder}/${Date.now()}_${id}.${ext}`;
  }

  async _sha1(file) {
    const buffer = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-1', buffer);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  async _authorize() {
    if (this._authCache && Date.now() < this._authCache.expiresAt) {
      return this._authCache;
    }

    const credentials = btoa(`${Env.backblazeKeyId}:${Env.backblazeAppKey}`);
    const response = await fetch('https://api.backblazeb2.com/b2api/v2/b2_authorize_account', {
      headers: { Authorization: `Basic ${credentials}` },
    });

    if (!response.ok) {
      throw new Error('فشل الاتصال بـ Backblaze B2');
    }

    const data = await response.json();
    this._authCache = { ...data, expiresAt: Date.now() + 23 * 60 * 60 * 1000 };
    return this._authCache;
  }

  async _getUploadUrl(auth) {
    const response = await fetch(`${auth.apiUrl}/b2api/v2/b2_get_upload_url`, {
      method: 'POST',
      headers: {
        Authorization: auth.authorizationToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ bucketId: Env.backblazeBucketId }),
    });

    if (!response.ok) {
      throw new Error('فشل الحصول على رابط الرفع');
    }

    return response.json();
  }

  async _uploadViaWorker(file, folder) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await fetch(`${Env.cloudflareWorkerUrl}/upload`, {
      method: 'POST',
      headers: { 'X-Khidma-Token': Env.cloudflareSecretToken },
      body: formData,
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.fileId || data.fileName || null;
  }

  async _uploadDirect(file, folder) {
    const auth = await this._authorize();
    const uploadData = await this._getUploadUrl(auth);
    const b2Folder = this._resolveFolder(folder, file);
    const fileName = this._buildFileName(b2Folder, file);
    const sha1 = await this._sha1(file);

    const response = await fetch(uploadData.uploadUrl, {
      method: 'POST',
      headers: {
        Authorization: uploadData.authorizationToken,
        'X-Bz-File-Name': encodeURIComponent(fileName),
        'X-Bz-Content-Sha1': sha1,
        'Content-Type': file.type || 'application/octet-stream',
        'Content-Length': String(file.size),
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error('فشل رفع الملف إلى Backblaze B2');
    }

    return fileName;
  }

  async upload(file, folder, { returnFullUrl = false } = {}) {
    if (!file) return null;

    let fileId = null;

    try {
      fileId = await this._uploadViaWorker(file, this._resolveFolder(folder, file));
    } catch (_) {}

    if (!fileId) {
      fileId = await this._uploadDirect(file, folder);
    }

    return returnFullUrl ? getBackblazeDownloadUrl(fileId) : fileId;
  }

  async uploadImage(file, folder) {
    return this.upload(file, folder, { returnFullUrl: true });
  }

  async uploadFile(file, folder) {
    return this.upload(file, folder, { returnFullUrl: false });
  }
}

export default new BackblazeService();
